/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { useFormik } from 'formik';

import { Button, Stack, Typography } from '@mui/material';
import { useEmailPhoneName } from '../../../../util/useEmailPhoneName';
import Input from '../../../../components/Input';
import { z } from 'zod';
import { isInvalidName } from '../../../../util/validateName';
import { isInvalidEmail } from '../../../../util/validateEmail';
import { isInvalidPhoneNumber } from '../../../../util/validatePhoneNumber';
import { ModalActions } from '../../../../components/Modal';
import { useDrawdownRequestContext } from './DrawdownRequestContext';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';
import { colors } from '@farmlands/mui-theme';

const Step3 = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const requiresReference = !!account?.captureCustomerReference;
  const { email, name, phone } = useEmailPhoneName();
  const { state, setState, salesAgreement } = useDrawdownRequestContext();

  const validationSchema = z.object({
    name: z
      .string()
      .min(1, 'Name is required')
      .refine((value) => !isInvalidName(value), {
        message: 'Please enter a valid name',
      }),
    email: z
      .string()
      .min(1, 'Email address is required')
      .email('Please enter a valid email address')
      .refine((value) => !isInvalidEmail(value), {
        message: 'Please enter a valid email address',
      }),
    phone: z
      .string()
      .min(1, 'Phone number is required')
      .refine((value) => !isInvalidPhoneNumber(value), {
        message: 'Please enter a valid phone number',
      }),
    reference: requiresReference
      ? z.string().max(60, 'Maximum 60 characters allowed').min(1, 'Reference is required')
      : z.string().max(60, 'Maximum 60 characters allowed').optional(),
  });

  const formik = useFormik({
    initialValues: {
      name: state.contactDetails?.name ?? name,
      email: state.contactDetails?.email ?? email,
      phone: state.contactDetails?.phone ?? phone,
      reference: state.contactDetails?.reference ?? salesAgreement?.customerReference ?? '',
    },
    validate: (values) => {
      const validation = validationSchema.safeParse(values); // Use safeParse for validation
      if (!validation.success) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return validation.error.errors.reduce((acc: any, curr: any) => {
          acc[curr.path[0]] = curr.message;
          return acc;
        }, {});
      }
    },
    onSubmit: (values) => {
      // Update context with Formik values
      setState({
        tabIndex: 3,
        contactDetails: values as {
          name: string;
          email: string;
          phone: string;
          reference?: string;
        },
      });
    },
  });

  const isTfo = Boolean(account?.hasTfoAccess);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Typography variant='body2' color={colors.primary700} fontWeight='bold'>
        {!isTfo ? 'Enter your details' : 'Enter customer details'}
      </Typography>
      <Typography variant='body2'>
        {!isTfo ? 'Confirm account details' : 'Confirm the recipients details'}
      </Typography>

      <Stack gap={0.5} mt={1}>
        {!isTfo && (
          <Stack p={1} bgcolor={colors.primary100} borderRadius={1} my={2}>
            <Stack pl={1} gap={0.5} borderLeft={`4px solid ${colors.grey900}`}>
              <Typography color={colors.primary700} variant='body2' fontWeight='bold'>
                {account?.accountName}
                <Typography color={colors.primary700} variant='body2' component='span' pl={1}>
                  ({account?.accountNumber})
                </Typography>
              </Typography>
              <Typography variant='body2' color={colors.primary700}>
                {formik.values.name}
              </Typography>
              <Typography variant='body2' color={colors.primary700}>
                {formik.values.email}
              </Typography>
            </Stack>
          </Stack>
        )}
        {isTfo && (
          <>
            <Input
              testId=''
              label='Name'
              name='name'
              type='text'
              disabled={!account?.hasTfoAccess}
              onChange={formik.handleChange}
              value={formik.values.name}
              error={!!formik.errors.name}
              errorMessage={formik.errors.name}
              required={!account?.hasTfoAccess}
            />
            <Input
              testId=''
              label='Email address'
              name='email'
              type='email'
              disabled={!account?.hasTfoAccess}
              onChange={formik.handleChange}
              value={formik.values.email}
              required={!account?.hasTfoAccess}
              error={!!formik.errors.email}
              errorMessage={formik.errors.email}
            />
          </>
        )}
        <Input
          testId=''
          label='Phone number'
          name='phone'
          type='tel'
          onChange={formik.handleChange}
          value={formik.values.phone}
          required
          error={!!formik.errors.phone}
          errorMessage={formik.errors.phone}
        />
        <Input
          testId=''
          label={account?.hasTfoAccess ? 'Customer Reference' : 'Your Reference'}
          placeholder='e.g. Your Purchase Order reference if required'
          name='reference'
          type='text'
          required={requiresReference}
          onChange={formik.handleChange}
          value={formik.values.reference}
          inputProps={{ maxLength: 60 }}
          error={!!formik.errors.reference}
          helperText={
            formik.errors.reference
              ? formik.errors.reference
              : `${formik.values.reference.length}/60 characters`
          }
        />
      </Stack>

      <ModalActions showDivider>
        <Button
          key='next-step-btn'
          variant='contained'
          onClick={() => formik.handleSubmit()}
          data-testid='TODO:test'
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next
        </Button>
      </ModalActions>
    </form>
  );
};

export default Step3;
